/**
 * RFI Messages Store Slice
 *
 * Zustand slice for RFI message thread state management.
 * Domain: ServicesContext > RFIMessagingAggregate
 *
 * Manages message threads keyed by rfiId with unread counts.
 * Appends new messages optimistically on send.
 */

import { clearError as clearOpError, isOperationLoading, setError, setLoading } from '@gw/ui';

import { rfiMessagesApi } from '@/api/rfiMessages';
import type { RFIMessage } from '@/types/rfi-message.types';

import type { ErrorStates, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface RFIMessagesSlice {
  // Message threads keyed by rfiId
  messagesByRFI: Record<string, RFIMessage[]>;
  messagesCursorByRFI: Record<string, string | null>;
  unreadCountsByRFI: Record<string, number>;
  // Loading/error
  rfiMessagesIsLoading: LoadingStates;
  rfiMessagesErrors: ErrorStates;
  // Actions
  fetchMessages: (rfiId: string, params?: { cursor?: string; limit?: number }) => Promise<void>;
  sendMessage: (rfiId: string, content: string) => Promise<void>;
  markRead: (rfiId: string, messageId: string) => Promise<void>;
  clearMessagesForRFI: (rfiId: string) => void;
  clearRFIMessagesError: (operation?: string) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialRFIMessagesState = {
  messagesByRFI: {},
  messagesCursorByRFI: {},
  unreadCountsByRFI: {},
  rfiMessagesIsLoading: {},
  rfiMessagesErrors: {},
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createRFIMessagesSlice: StateCreator<RFIMessagesSlice, [], [], RFIMessagesSlice> = (
  set,
  get
) => ({
  ...initialRFIMessagesState,

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Fetch messages for an RFI thread. Appends to existing messages for pagination.
   */
  fetchMessages: async (rfiId: string, params?: { cursor?: string; limit?: number }) => {
    const operation = `fetchMessages-${rfiId}`;
    const currentState = get();

    if (isOperationLoading(currentState.rfiMessagesIsLoading, operation)) return;

    set({
      rfiMessagesIsLoading: setLoading(currentState.rfiMessagesIsLoading, operation, true),
      rfiMessagesErrors: clearOpError(currentState.rfiMessagesErrors, operation),
    });

    try {
      const response = await rfiMessagesApi.listMessages(rfiId, params);

      const state = get();
      const existingMessages = params?.cursor ? (state.messagesByRFI[rfiId] ?? []) : [];

      set({
        messagesByRFI: {
          ...state.messagesByRFI,
          [rfiId]: [...existingMessages, ...response.messages],
        },
        messagesCursorByRFI: {
          ...state.messagesCursorByRFI,
          [rfiId]: response.nextCursor ?? null,
        },
        rfiMessagesIsLoading: setLoading(state.rfiMessagesIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiMessagesIsLoading: setLoading(state.rfiMessagesIsLoading, operation, false),
        rfiMessagesErrors: setError(
          state.rfiMessagesErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch messages'
        ),
      });
    }
  },

  /**
   * Send a message in an RFI thread and append to the thread
   */
  sendMessage: async (rfiId: string, content: string) => {
    const operation = `sendMessage-${rfiId}`;
    const currentState = get();

    if (isOperationLoading(currentState.rfiMessagesIsLoading, operation)) return;

    set({
      rfiMessagesIsLoading: setLoading(currentState.rfiMessagesIsLoading, operation, true),
      rfiMessagesErrors: clearOpError(currentState.rfiMessagesErrors, operation),
    });

    try {
      const message = await rfiMessagesApi.sendMessage(rfiId, content);

      const state = get();
      const existing = state.messagesByRFI[rfiId] ?? [];

      set({
        messagesByRFI: {
          ...state.messagesByRFI,
          [rfiId]: [...existing, message],
        },
        rfiMessagesIsLoading: setLoading(state.rfiMessagesIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiMessagesIsLoading: setLoading(state.rfiMessagesIsLoading, operation, false),
        rfiMessagesErrors: setError(
          state.rfiMessagesErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to send message'
        ),
      });
      throw error;
    }
  },

  /**
   * Mark a message as read and decrement unread count.
   * Returns early without making an API call if messageId is falsy.
   */
  markRead: async (rfiId: string, messageId: string) => {
    if (!messageId) return;

    const operation = `markRead-${rfiId}-${messageId}`;
    const currentState = get();

    if (isOperationLoading(currentState.rfiMessagesIsLoading, operation)) return;

    set({
      rfiMessagesIsLoading: setLoading(currentState.rfiMessagesIsLoading, operation, true),
      rfiMessagesErrors: clearOpError(currentState.rfiMessagesErrors, operation),
    });

    try {
      await rfiMessagesApi.markRead(rfiId, messageId);

      const state = get();
      const currentUnread = state.unreadCountsByRFI[rfiId] ?? 0;

      set({
        unreadCountsByRFI: {
          ...state.unreadCountsByRFI,
          [rfiId]: Math.max(0, currentUnread - 1),
        },
        rfiMessagesIsLoading: setLoading(state.rfiMessagesIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiMessagesIsLoading: setLoading(state.rfiMessagesIsLoading, operation, false),
        rfiMessagesErrors: setError(
          state.rfiMessagesErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to mark message as read'
        ),
      });
    }
  },

  /**
   * Clear all messages and cursor for a specific RFI thread
   */
  clearMessagesForRFI: (rfiId: string) => {
    const state = get();
    const { [rfiId]: _msgs, ...remainingMessages } = state.messagesByRFI;
    const { [rfiId]: _cursor, ...remainingCursors } = state.messagesCursorByRFI;

    set({
      messagesByRFI: remainingMessages,
      messagesCursorByRFI: remainingCursors,
    });
  },

  /**
   * Clear error for a specific operation or all errors
   */
  clearRFIMessagesError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({
        rfiMessagesErrors: clearOpError(currentState.rfiMessagesErrors, operation),
      });
    } else {
      set({ rfiMessagesErrors: {} });
    }
  },
});
